import { watch } from 'vue'
import { useAuth } from '@/composables/useAuth'

/**
 * Zendesk Web Widget loader.
 *
 * A single widget is shown at a time, chosen by whether the user is signed in:
 *  - authenticated (member) → the member ("adhérent") widget;
 *  - not authenticated → the prospect widget.
 *
 * The auth state flips client-side (login/logout, cross-tab token changes) with
 * no full reload, and Zendesk offers no supported way to swap the widget key at
 * runtime, so a state change tears the current widget down and re-injects the
 * snippet for the other key.
 *
 * Teardown must fully remove the previous widget — including an open chat window —
 * otherwise its iframes pile up on every switch and the stale window lingers with
 * no way to close it. Zendesk drops a direct-child iframe on `<body>` plus a
 * container `<div>` holding the launcher and chat-window iframes; both must go.
 * We can't rely on Zendesk's DOM markers (iframe titles are localized, `src`s are
 * blank), so teardown removes every `<body>` child that is — or contains — a
 * Zendesk iframe, evaluated when the iframes actually exist. The app keeps its
 * content inside `#app` and only teleports iframe-free elements, so its DOM is
 * never touched.
 *
 * Call once, from the root component's setup. No-op during SSR (browser only).
 */

/** Zendesk Web Widget key of the member ("adhérent") account (signed-in user). */
const ADHERENT_KEY = '2a967477-b7ba-4cf1-8b15-f15f74a31565'
/** Zendesk Web Widget key of the prospect account (signed-out visitor). */
const PROSPECT_KEY = '4724b507-f5ee-474e-8797-4d0cf88a9bdf'
const SNIPPET_SRC = 'https://static.zdassets.com/ekr/snippet.js'

/** Globals the Zendesk snippet defines; cleared on teardown so a fresh snippet
 *  (with a different key) can re-initialise cleanly. */
const ZENDESK_GLOBALS = ['zE', 'zEmbed', 'zESettings', '$zopim', 'zEACLoaded'] as const

/** Key of the widget currently loaded, or null when none is present. */
let currentKey: string | null = null

/** Remove the current widget entirely: close its open window, drop the DOM it
 *  injected, and clear its globals. Best effort — never throws. */
function teardown(): void {
  // Ask the widget to close first (harmless if already closed); its DOM is
  // removed right after anyway, but this also stops any pending open animation.
  try {
    const zE = (window as unknown as { zE?: (...args: unknown[]) => void }).zE
    if (typeof zE === 'function') {
      zE('messenger', 'close')
      zE('webWidget', 'close')
    }
  } catch {
    /* API not ready — the node removal below is what actually dismisses it. */
  }
  // Zendesk under <body>: a bare iframe plus a container <div> holding the
  // launcher/chat iframes. Anything that is — or contains — an iframe is the
  // widget's; the app's teleported dialogs/sheets never contain one.
  Array.from(document.body.children).forEach((el) => {
    if (el.id === 'app') return
    const isZendesk =
      el.tagName === 'IFRAME' ||
      !!el.querySelector('iframe') ||
      (el.tagName === 'SCRIPT' && /zdassets|zendesk|zopim/i.test((el as HTMLScriptElement).src))
    if (isZendesk) el.remove()
  })
  // Snippet + chunk scripts/styles Zendesk injected into <head>.
  document
    .querySelectorAll('#ze-snippet, script[src*="zdassets"], script[src*="zendesk"]')
    .forEach((el) => el.remove())
  const win = window as unknown as Record<string, unknown>
  for (const name of ZENDESK_GLOBALS) win[name] = undefined
  currentKey = null
}

/** Load the Zendesk widget for `key`, replacing any previously loaded one. */
function loadWidget(key: string): void {
  if (currentKey === key) return
  if (currentKey) teardown()
  const script = document.createElement('script')
  script.id = 'ze-snippet'
  script.async = true
  script.src = `${SNIPPET_SRC}?key=${key}`
  document.head.appendChild(script)
  currentKey = key
}

export function useZendesk(): void {
  const { isAuthenticated } = useAuth()
  // Browser-only: the widget must never be injected during SSG pre-rendering.
  if (typeof document === 'undefined') return
  watch(
    () => isAuthenticated.value,
    (authenticated) => loadWidget(authenticated ? ADHERENT_KEY : PROSPECT_KEY),
    { immediate: true },
  )
}
